'use client';

import { Button, DropdownMenu, Input } from '@common/components';
import SvgChevronup from '@common/icon/SvgChevronup';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { HOUSEHOLD_OPTIONS } from '@/app/(allowed)/median-income/constants';
import { ROUTES } from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { hasWelloBottomNav } from '@/utils';

import type { HouseholdSize, MedianIncomeState } from './useMedianIncomeState';

interface MobileStickyCalculatorProps {
  state: MedianIncomeState;
  /** 상세 페이지 등에서 promo 용도로 쓸 때 하단 "내 중위소득 확인하기 →" CTA 노출. */
  showNavCta?: boolean;
}

/**
 * 모바일/태블릿 하단 sticky 계산기 — 헤더 toggle 로 펼침/접힘. 초기 펼침.
 * PC에서는 우측 사이드바의 `<Calculator />`가 같은 state 를 공유한다.
 */
export const MobileStickyCalculator = ({
  state,
  showNavCta,
}: MobileStickyCalculatorProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const pathname = usePathname();
  const hideBottomNav = useGlobalStore(
    (globalState) => globalState.hideBottomNav,
  );
  const hasBottomNav = hasWelloBottomNav(pathname, hideBottomNav);
  const {
    householdSize,
    amountInput,
    percentInput,
    setHouseholdSize,
    onAmountChange,
    onPercentChange,
  } = state;

  const householdLabel =
    HOUSEHOLD_OPTIONS[householdSize - 1]?.label ?? `${householdSize}인`;
  const summary = `${householdLabel} · ${amountInput || '0'}만원 · ${percentInput || '0'}%`;

  return (
    <div
      className={cn(
        'fixed inset-x-0 hidden border-t-1 border-Gray-200 bg-Gray-white mobile-tablet:block',
        //* 연말 이벤트 한정(WEL-5478) — 플로팅 BNB(높이 58 + 하단 10) 뒤까지 패널을 내려 알약 주변에 본문이 비치지 않게 한다. 채널은 첫 페인트 전 data-partner로 가른다
        hasBottomNav
          ? 'owns-safe-area bottom-0 z-[9] pb-[calc(68px+var(--safe-area-bottom,0px))] kbpay:bottom-[calc(70px+var(--safe-area-bottom,0px))] kbpay:z-20 kbpay:pb-0 toss:bottom-[calc(70px+var(--safe-area-bottom,0px))] toss:z-20 toss:pb-0 kakaobank:bottom-[calc(70px+var(--safe-area-bottom,0px))] kakaobank:z-20 kakaobank:pb-0'
          : 'bottom-[calc(70px+var(--safe-area-bottom,0px))] z-20',
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between gap-12 bg-transparent px-16 py-12 text-left"
      >
        <span className="truncate font-700 text-Gray-900 typo-body4">
          {summary}
        </span>
        <SvgChevronup
          aria-hidden="true"
          className={cn(
            'size-16 text-Gray-700 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-8 px-16 pb-12">
            <Row label="가구원" htmlFor="mobile-calc-hh">
              <DropdownMenu
                dimensions="H40"
                options={HOUSEHOLD_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                value={householdSize}
                onChange={(v) => setHouseholdSize(v as HouseholdSize)}
                placeholder="가구원 수 선택"
              />
            </Row>
            <Row label="가구원 월소득" htmlFor="mobile-calc-amount">
              <Input
                id="mobile-calc-amount"
                type="local-string-number"
                inputMode="numeric"
                dimensions="H40"
                value={amountInput}
                onChange={(e) => onAmountChange(e.target.value)}
                placeholder="예: 300"
                rightContent={
                  <span className="font-400 text-Gray-600 typo-caption1">
                    만원
                  </span>
                }
              />
            </Row>
            <Row label="중위소득 비율" htmlFor="mobile-calc-pct">
              <Input
                id="mobile-calc-pct"
                type="text"
                inputMode="decimal"
                dimensions="H40"
                value={percentInput}
                onChange={(e) => onPercentChange(e.target.value)}
                placeholder="예: 100"
                rightContent={
                  <span className="font-400 text-Gray-600 typo-caption1">
                    %
                  </span>
                }
              />
            </Row>
            {showNavCta ? (
              <Button asChild dimensions="H40" className="mt-4 w-full">
                <CustomLink href={ROUTES.MEDIAN_INCOME.pathname}>
                  내 중위소득 확인하기 →
                </CustomLink>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

interface RowProps {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}

const Row = ({ label, htmlFor, children }: RowProps) => (
  <div className="flex items-center gap-12">
    <label
      htmlFor={htmlFor}
      className="w-80 shrink-0 text-Gray-700 typo-caption1"
    >
      {label}
    </label>
    <div className="flex-1">{children}</div>
  </div>
);
